import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { EmptyShareState, PublicPageCTA, PublicShareLayout } from '../../components/PublicShare/PublicShareLayout';
import ShareMenu from '../../components/PublicShare/ShareMenu';
import { PublicShareService } from '../../services/publicShareService';
import { useShareMetadata } from '../../utils/shareMetadata';
import styles from './PublicWatchlistShare.module.css';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';

export default function PublicWatchlistShare() {
    const { username } = useParams();
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        PublicShareService.getWatchlist(username)
            .then((data) => {
                if (!mounted) return;
                setPayload(data);
                if (!data.isPrivate) {
                    trackEvent(ANALYTICS_EVENTS.PUBLIC_WATCHLIST_VIEWED, {}, { onceKey: `watchlist:${username}` });
                }
            })
            .catch((err) => mounted && setError(err.message || 'Watchlist not found'));
        return () => { mounted = false; };
    }, [username]);

    const ownerName = payload?.user?.displayName || payload?.user?.username || username;
    const metadata = useMemo(() => ({
        title: `${ownerName}'s Watchlist | Aluvo`,
        description: `See what's on ${ownerName}'s Aluvo watchlist.`,
        url: `${window.location.origin}/u/${username}/watchlist`,
        image: `${window.location.origin}/og-image.png`,
        text: `Check out ${ownerName}'s Aluvo watchlist 🍿`,
    }), [ownerName, username]);
    useShareMetadata(metadata);

    if (error) return <EmptyShareState icon="🍿" title="Watchlist not found" message="This watchlist is not available." />;
    if (!payload) return <EmptyShareState icon="◎" title="Loading watchlist" message="Gathering movies and shows..." />;
    if (payload.isPrivate) return <EmptyShareState icon="🔒" title="This watchlist is private." message="The owner has chosen not to share its contents." />;

    return (
        <PublicShareLayout>
            <header className={styles.hero}>
                <span className={styles.eyebrow}>MY WATCHLIST</span>
                <h1>{ownerName}'s Watchlist</h1>
                <p>@{payload.user.username} · {payload.count} {payload.count === 1 ? 'title' : 'titles'}</p>
                <ShareMenu {...metadata} compact />
            </header>

            {payload.items.length ? (
                <section className={styles.grid} aria-label="Watchlist titles">
                    {payload.items.map((item, index) => {
                        const card = (
                            <article className={styles.card}>
                                {item.posterUrl
                                    ? <img src={item.posterUrl} alt={`${item.title} poster`} loading="lazy" />
                                    : <div className={styles.placeholder}>🍿</div>}
                                <h2>{item.title}</h2>
                                <p>{item.year || (item.kind === 'tv' ? 'TV series' : 'Movie')}</p>
                            </article>
                        );
                        return item.tmdbId
                            ? <Link className={styles.link} to={`/movie/${item.kind}/${item.tmdbId}`} key={`${item.kind}-${item.tmdbId}-${index}`}>{card}</Link>
                            : <div key={index}>{card}</div>;
                    })}
                </section>
            ) : <EmptyShareState icon="🍿" title="Nothing saved yet" message="This public watchlist is empty." />}

            <PublicPageCTA title="ALUVO" text="Build your own movie and TV watchlist, then verify what you watch." />
        </PublicShareLayout>
    );
}
